import React from 'react';
import { CustomFeature, farsiLabels } from '@/types/brokers/slug';
import { TickCircle, InfoCircle, CloseCircle } from 'iconsax-react';
import { exchangeFarsiLabels } from '@/types/exchanges/page';
import Image from 'next/image';
import { GoldFarsiLabels } from '@/types/gold/page';

interface BrokerFieldsTableProps {
    type: 'foreign' | 'iranian' | 'props' | 'broker' | 'gold' | undefined;
    mainItems: {
        key: string;
        value: any;
        Icon?: React.ComponentType<any>; // Updated to the IconType alias
    }[];
    customFeatures: CustomFeature[];
    countryFlag?: string | null;
}

const FieldsTableFeatures: React.FC<BrokerFieldsTableProps> = ({
    mainItems,
    type,
    customFeatures,
    countryFlag,
}) => {
    // A plain list: the dialog / bottom sheet around it scrolls (the old fixed 300px box cut
    // the list short and scrolled inside a scroll).
    return (
        <ul className='divide-y divide-gray-100 dark:divide-white/10'>
            {mainItems.map((item) => (
                <li key={item.key} className='flex items-center justify-between gap-4 py-3'>
                    <span className='flex min-w-0 items-center gap-3 text-sm text-gray-700 dark:text-white/80'>
                        <span className='flex size-9 shrink-0 items-center justify-center rounded-xl bg-progress/10 text-progress dark:bg-white/[0.06]'>
                            {countryFlag && item.key === 'nameFa' ? (
                                <Image
                                    src={countryFlag}
                                    alt=''
                                    width={22}
                                    height={22}
                                    className='size-[22px] rounded-full object-cover'
                                />
                            ) : item.Icon ? (
                                React.isValidElement(item.Icon) ? (
                                    item.Icon
                                ) : (
                                    <item.Icon className='size-5' size={20} stroke='currentColor' />
                                )
                            ) : null}
                        </span>
                        <span className='truncate'>
                            {type === 'broker'
                                ? farsiLabels[item.key]
                                : type === 'gold'
                                  ? GoldFarsiLabels[item.key]
                                  : exchangeFarsiLabels[item.key]}
                        </span>
                    </span>
                    <FeatureValue value={item.value} />
                </li>
            ))}
            {customFeatures.map((feature, index) => (
                <li key={`c-${index}`} className='flex items-center justify-between gap-4 py-3'>
                    <span className='flex min-w-0 items-center gap-3 text-sm text-gray-700 dark:text-white/80'>
                        <span className='flex size-9 shrink-0 items-center justify-center rounded-xl bg-progress/10 text-progress dark:bg-white/[0.06]'>
                            <InfoCircle size={20} stroke='currentColor' />
                        </span>
                        <span className='truncate'>{feature.title}</span>
                    </span>
                    <FeatureValue value={feature.value === 'دارد' ? true : feature.value} />
                </li>
            ))}
        </ul>
    );
};

/** Yes / no as small badges, anything else as bold text */
const FeatureValue: React.FC<{ value: unknown }> = ({ value }) => {
    if (typeof value === 'number' || (typeof value === 'string' && value !== '')) {
        return (
            <span className='shrink-0 text-end text-sm font-semibold text-gray-900 dark:text-white'>
                {value === 0 ? '' : String(value)}
            </span>
        );
    }
    return value ? (
        <span className='inline-flex shrink-0 items-center gap-1 rounded-full bg-green-500/10 px-2.5 py-1 text-xs font-medium text-green-700 dark:text-green-400'>
            <TickCircle
                size={14}
                className='fill-green-500 stroke-white dark:stroke-primary-active'
            />
            دارد
        </span>
    ) : (
        <span className='inline-flex shrink-0 items-center gap-1 rounded-full bg-red-500/10 px-2.5 py-1 text-xs font-medium text-red-600 dark:text-red-400'>
            <CloseCircle
                size={14}
                className='fill-red-500 stroke-white dark:stroke-primary-active'
            />
            ندارد
        </span>
    );
};

export default FieldsTableFeatures;
